import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ReferralReward {
  id: string;
  code: string;
  valid_until: string | null;
  is_active: boolean;
  current_uses: number | null;
  max_uses: number | null;
}

export interface ReferralData {
  code: string;
  successful_count: number;
  total_rewards_issued: number;
  current_cycle: number; // 0..9
  shareUrl: string;
  rewards: ReferralReward[];
}

const SITE = "https://iamerica.app";

export function useReferral(userId: string | undefined) {
  return useQuery<ReferralData | null>({
    queryKey: ["referral-code", userId],
    enabled: !!userId,
    queryFn: async () => {
      if (!userId) return null;

      const { data: row, error } = await supabase
        .from("referral_codes" as any)
        .select("code, successful_count, total_rewards_issued")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      if (!row) return null;

      const r = row as any;

      const { data: rewards } = await supabase
        .from("coupons")
        .select("id, code, valid_until, is_active, current_uses, max_uses, description")
        .like("description", `REFERRAL_REWARD:${userId}:%`)
        .order("created_at", { ascending: false });

      const activeRewards = (rewards || []).filter((c: any) =>
        c.is_active && (c.max_uses == null || (c.current_uses || 0) < c.max_uses) &&
        (!c.valid_until || new Date(c.valid_until) > new Date())
      );

      return {
        code: r.code,
        successful_count: r.successful_count,
        total_rewards_issued: r.total_rewards_issued,
        current_cycle: r.successful_count % 10,
        shareUrl: `${SITE}/ref/${r.code}`,
        rewards: activeRewards as ReferralReward[],
      };
    },
  });
}
